/**
 * The shelf's pure model (1.AB-2 deliverable 13): its kinds, the URL codec, the catalogue query
 * of a view and the one-line summary of a row.
 */
import type { CatalogueFilterV1, CatalogueQueryV1 } from '@codex/contracts';

export type ShelfKind = 'creatures' | 'spells' | 'items';

export interface ShelfKindInfo {
  readonly key: ShelfKind;
  readonly typeKey: 'ref_creature' | 'ref_spell' | 'ref_item';
  readonly label: string;
  readonly singular: string;
  /** The type's glyph in the kit. */
  readonly glyph: 'creature' | 'spell' | 'item';
}

const CREATURES: ShelfKindInfo = {
  key: 'creatures',
  typeKey: 'ref_creature',
  label: 'Creatures',
  singular: 'Creature',
  glyph: 'creature',
};

export const SHELF_KINDS: readonly ShelfKindInfo[] = [
  CREATURES,
  { key: 'spells', typeKey: 'ref_spell', label: 'Spells', singular: 'Spell', glyph: 'spell' },
  { key: 'items', typeKey: 'ref_item', label: 'Items', singular: 'Item', glyph: 'item' },
];

export const DEFAULT_KIND: ShelfKind = 'creatures';
export const SHELF_PAGE_SIZE = 50;
export const SHELF_Q_MAX = 200;

export interface CrBand {
  readonly id: string;
  readonly label: string;
  readonly gte: number;
  readonly lte: number | null;
}

/** The challenge-rating bands (the lower bound is inclusive: `gte`, never `gt`). */
export const CR_BANDS: readonly CrBand[] = [
  { id: '0-4', label: '0 to 4', gte: 0, lte: 4 },
  { id: '5-10', label: '5 to 10', gte: 5, lte: 10 },
  { id: '11-16', label: '11 to 16', gte: 11, lte: 16 },
  { id: '17-up', label: '17 and up', gte: 17, lte: null },
];

const ORDINALS = [
  'Cantrip',
  '1st',
  '2nd',
  '3rd',
  '4th',
  '5th',
  '6th',
  '7th',
  '8th',
  '9th',
] as const;
export const LEVEL_OPTIONS: readonly { readonly value: number; readonly label: string }[] =
  ORDINALS.map((label, value) => ({ value, label }));

/** The state of the shelf, all of it in the URL. */
export interface ShelfView {
  readonly kind: ShelfKind;
  readonly q: string;
  readonly cr: string | null;
  readonly level: number | null;
  readonly class: string | null;
  readonly rarity: string | null;
}

export const EMPTY_VIEW: ShelfView = {
  kind: DEFAULT_KIND,
  q: '',
  cr: null,
  level: null,
  class: null,
  rarity: null,
};

/** The URL's search object: only what differs from the default view. */
export interface ShelfSearch {
  kind?: ShelfKind;
  q?: string;
  cr?: string;
  level?: number;
  class?: string;
  rarity?: string;
}

const FILTER_MAX = 60;

function textOf(value: unknown, max: number): string | null {
  const text =
    typeof value === 'string'
      ? value
      : typeof value === 'number' && Number.isFinite(value)
        ? String(value)
        : null;
  if (text === null || text.trim() === '') return null;
  return text.length > max ? null : text;
}

function levelOf(value: unknown): number | null {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && /^\d$/.test(value)
        ? Number(value)
        : NaN;
  return Number.isInteger(n) && n >= 0 && n <= 9 ? n : null;
}

/**
 * Reads the URL's search into a view. Unknown or malformed params are dropped, and a filter that
 * belongs to another kind is ignored; it never throws, whatever it is given.
 */
export function parseShelfSearch(raw: unknown): ShelfView {
  const source: Record<string, unknown> =
    typeof raw === 'object' && raw !== null && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  const kind = SHELF_KINDS.find((k) => k.key === source.kind)?.key ?? DEFAULT_KIND;
  const q = textOf(source.q, SHELF_Q_MAX) ?? '';
  const band = textOf(source.cr, FILTER_MAX);
  return {
    kind,
    q,
    cr: kind === 'creatures' && band !== null && CR_BANDS.some((b) => b.id === band) ? band : null,
    level: kind === 'spells' ? levelOf(source.level) : null,
    class: kind === 'spells' ? textOf(source.class, FILTER_MAX) : null,
    rarity: kind === 'items' ? textOf(source.rarity, FILTER_MAX) : null,
  };
}

/** The search object of a view; defaults are left out, so the plain shelf is `/reference`. */
export function writeShelfSearch(view: ShelfView): ShelfSearch {
  const out: ShelfSearch = {};
  if (view.kind !== DEFAULT_KIND) out.kind = view.kind;
  if (view.q.trim() !== '') out.q = view.q;
  if (view.kind === 'creatures' && view.cr !== null) out.cr = view.cr;
  if (view.kind === 'spells') {
    if (view.level !== null) out.level = view.level;
    if (view.class !== null) out.class = view.class;
  }
  if (view.kind === 'items' && view.rarity !== null) out.rarity = view.rarity;
  return out;
}

/** Whether any filter (not the search text) is set. */
export function hasFilters(view: ShelfView): boolean {
  return view.cr !== null || view.level !== null || view.class !== null || view.rarity !== null;
}

export function kindInfo(kind: ShelfKind): ShelfKindInfo {
  return SHELF_KINDS.find((k) => k.key === kind) ?? CREATURES;
}

/** The `catalogue.query` body of a view (without a cursor). */
export function shelfQuery(view: ShelfView): CatalogueQueryV1 {
  const filters: CatalogueFilterV1[] = [];
  if (view.kind === 'creatures' && view.cr !== null) {
    const band = CR_BANDS.find((b) => b.id === view.cr);
    if (band) {
      filters.push({ column: 'cr', op: 'gte', value: band.gte });
      if (band.lte !== null) filters.push({ column: 'cr', op: 'lte', value: band.lte });
    }
  }
  if (view.kind === 'spells') {
    if (view.level !== null) filters.push({ column: 'level', op: 'eq', value: view.level });
    if (view.class !== null) filters.push({ column: 'classes', op: 'eq', value: view.class });
  }
  if (view.kind === 'items' && view.rarity !== null) {
    filters.push({ column: 'rarity', op: 'eq', value: view.rarity });
  }
  const q = view.q.trim();
  return {
    ...(q === '' ? {} : { q }),
    filters,
    sort: [{ column: 'title', direction: 'asc' }],
    limit: SHELF_PAGE_SIZE,
  };
}

const FRACTIONS: Readonly<Record<string, string>> = { '0.125': '1/8', '0.25': '1/4', '0.5': '1/2' };

function first(values: Readonly<Record<string, readonly unknown[]>>, column: string): unknown {
  return values[column]?.[0];
}

/** "CR 2", "3rd-level evocation" or "Cantrip", "Uncommon"; empty when the data is missing. */
export function rowSummary(
  kind: ShelfKind,
  values: Readonly<Record<string, readonly unknown[]>>,
): string {
  if (kind === 'creatures') {
    const cr = first(values, 'cr');
    if (typeof cr !== 'number') return '';
    return `CR ${FRACTIONS[String(cr)] ?? String(cr)}`;
  }
  if (kind === 'spells') {
    const level = first(values, 'level');
    if (typeof level !== 'number' || !Number.isInteger(level) || level < 0 || level > 9) return '';
    if (level === 0) return 'Cantrip';
    const school = first(values, 'school');
    const head = `${ORDINALS[level]}-level`;
    return typeof school === 'string' && school !== '' ? `${head} ${school.toLowerCase()}` : head;
  }
  const rarity = first(values, 'rarity');
  return typeof rarity === 'string' ? rarity : '';
}
